import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Container,
  Toolbar,
  Box,
  IconButton,
  Button,
  ListItemIcon,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Badge,
  Typography,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  PlaceOutlined as LocationIcon,
  HomeOutlined as HomeIcon,
  CategoryOutlined as ServicesIcon,
  ContactMailOutlined as ContactIcon,
  GroupOutlined as ProviderIcon,
  PersonOutline as ProfileIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../routines/user-operations";
import { useSetLoading } from "../../hooks/appearance";
import MemgoLogo from "../../../assets/media/memgo-logo.svg";
import EnableLocation from "../enable-location-component/enable-location";
import { getCartCount } from "../../../api/cart.api";
import ShoppingCart from "../../../assets/icons/bag.png";
import { ReactComponent as UserIcon } from "../../../assets/icons/user-profile-icon.svg";
import { getUserProfileDetails } from "../../../api/userProfile.api";
import AlertComponent from "../alert-component/alert-component";

export default function HeaderNew() {
  // HOOKS & CONSTANTS

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);
  const userDetailStore = useSelector(
    (state: any) => state.session?.userDetails?.user,
  );

  const { city } = useSelector((state: any) => state.userLocation);
  const cartRedux = useSelector((state: any) => state.cart?.count);

  // STATES
  const [isLoading, setIsLoading] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [anchorProfileEl, setAnchorProfileEl] = useState<null | HTMLElement>(
    null,
  );
  const [userProfileDetails, setUserProfileDetails] = useState<any>(null);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [alertType, setAlertType] = useState<
      "error" | "success" | "info" | "warning"
    >("info");
  // LOCAL STORAGE USER DETAILS
  let parsedUserDetails: any = null;
  if (isLoggedIn) {
    const userDetailsRaw = localStorage.getItem("userDetails");
    try {
      if (userDetailsRaw) parsedUserDetails = JSON.parse(userDetailsRaw);
    } catch (error) {
      console.error("Failed to parse userDetails:", error);
    }
  }

  // LOADING STATE EFFECT
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  // NAVIGATION LINKS
  const navLinks = [
    { label: "Home", path: "/home", icon: <HomeIcon /> },
    { label: "Services", path: "/services", icon: <ServicesIcon /> },
    { label: "Contact Us", path: "/contact-us", icon: <ContactIcon /> },
    { label: "Service Providers", path: "/become-provider", icon: <ProviderIcon /> },
  ];

  // HANDLERS
  const toggleMobileDrawer = (open: boolean) => () => setMobileDrawerOpen(open);

  const finalUserName = useMemo(() => {
    const firstName =
      userProfileDetails?.first_name ||
      userDetailStore?.first_name ||
      parsedUserDetails?.user?.first_name ||
      "";

    const lastName =
      userProfileDetails?.last_name ||
      userDetailStore?.last_name ||
      parsedUserDetails?.user?.last_name ||
      "";

    return `${firstName} ${lastName}`.trim();
  }, [userProfileDetails, userDetailStore, parsedUserDetails]);

  const finalUserEmail = useMemo(() => {
    return (
      userProfileDetails?.email ||
      userDetailStore?.email ||
      parsedUserDetails?.user?.email ||
      ""
    );
  }, [userProfileDetails, userDetailStore, parsedUserDetails]);

  const finalProfilePhoto = (isLoggedIn && userDetailStore?.profile_photo) || "";

  const handleLogoClick = () => {
    sessionStorage.removeItem("providerFilters");

    if (location.pathname === "/home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/home", { state: { manualNav: true } });
    }
  };

  const handleHomeNavigation = () => {
    sessionStorage.removeItem("providerFilters");
    navigate("/home", { state: { manualNav: true } });
  };

  const handleEnableLocation = () => setOpenLocationDialog(true);
  const handleCloseLocationDialog = () => setOpenLocationDialog(false);

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      logout();
      setAnchorProfileEl(null);
      setAlertMessage("Logout successful.");
      setAlertType("success");
      setAlertOpen(true);
      navigate("/");
    } catch (error) {
      console.error("Logout error: ", error);
      setAlertMessage("Failed to logout.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  useSetLoading(showLoading);

  const fetchCartCount = async () => {
    if (!isLoggedIn) return;
    try {
      const response = await getCartCount();
      const count = response.data?.data?.data?.totalCount ?? 0;
      dispatch({ type: "CART:SET_COUNT", payload: { count } });
    } catch (error) {
      console.error("ERROR IN FETCHING CART COUNT::", error);
    }
  };

  const fetchProfile = async () => {
    if (!isLoggedIn) return;

    try {
      const response = await getUserProfileDetails();
      setUserProfileDetails(response.data?.data);
      if (response?.data?.success) {
        dispatch({
          type: "SESSION:UPDATE_USER_DETAILS",
          payload: {
            profile_photo: response.data.data.profile_photo,
          },
        });
      }
    } catch (error) {
      console.error("USER PROFILE GET FAILED::", error);
    }
  };

  // EFFECTS
  useEffect(() => {
    fetchCartCount();
  }, [isLoggedIn]);

  useEffect(() => {
    setAnchorProfileEl(null);
  }, [location.pathname]);

  // COMPUTED VALUES
  const openProfile = Boolean(anchorProfileEl);

  useEffect(() => {
    fetchProfile();
  }, [isLoggedIn, userDetailStore?.profile_photo]);

  return (
    <AppBar
      position="sticky"
      sx={{ backgroundColor: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
    >
      <Container
        maxWidth="xl"
        sx={{
          paddingRight: "0px !important",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            justifyContent: "space-between",
            py: 1,
            paddingBottom: 0,
            paddingRight: !isMobile ? 0.8 : "",
            paddingLeft: !isMobile ? 2 : "",
          }}
        >
          {/* Logo & Location */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={MemgoLogo}
              alt="AFP Logo"
              onClick={handleLogoClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleLogoClick();
              }}
              sx={{
                width: { xs: 120, sm: 150, md: 180, lg: 195 },
                height: "auto",
                mr: 2,
                cursor: "pointer",
              }}
            />
            <Box className="location-button-box" onClick={handleEnableLocation}>
              <LocationIcon className="location-icon" />
              <Typography className="enable-location-text">
                {city ? city.split(",")[0].trim() : "Enable Location"}
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              mr: { md: 2 },
            }}
          >
            {navLinks.map((link) =>
              link.path === "/home" ? (
                <Button
                  key={link.label}
                  onClick={handleHomeNavigation}
                  sx={{
                    mx: 1,
                    color: "#15585D !important",
                    border:
                      location.pathname === link.path
                        ? "1px solid #15585D"
                        : "",
                    textTransform: "none",
                    fontSize: "1rem",
                    padding: 0.5,
                    paddingLeft: 1,
                    paddingRight: 1,
                    borderRadius: "8px",
                    margin: {
                      lg: "0 1rem 0 1rem",
                    },
                    "&:hover, &:active, &:focus, &.Mui-focusVisible": {
                      color: "#15585D !important",
                      backgroundColor: "rgba(21, 88, 93, 0.04)",
                    },
                  }}
                >
                  {link.label}
                </Button>
              ) : (
                <Button
                  key={link.label}
                  component={Link}
                  to={link.path}
                  sx={{
                    mx: 1,
                    color: "#15585D !important",
                    border:
                      location.pathname === link.path
                        ? "1px solid #15585D"
                        : "",
                    textTransform: "none",
                    fontSize: "1rem",
                    padding: 0.5,
                    paddingLeft: 1,
                    paddingRight: 1,
                    borderRadius: "8px",
                    margin: {
                      lg: "0 1rem 0 1rem",
                    },
                    "&:hover, &:active, &:focus, &.Mui-focusVisible": {
                      color: "#15585D !important",
                      backgroundColor: "rgba(21, 88, 93, 0.04)",
                    },
                  }}
                >
                  {link.label}
                </Button>
              ),
            )}

            {isLoggedIn ? (
              <>
                {/*  Shopping Cart Icon */}
                <IconButton
                  component={Link}
                  to="/cart"
                  sx={{
                    borderRadius: "8px",
                    "&:hover": {
                      bgcolor: "rgba(21, 88, 93, 0.1)",
                    },
                  }}
                >
                  <Badge badgeContent={cartRedux} color="error">
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        bgcolor: "#15585D",
                        WebkitMask: `url(${ShoppingCart}) no-repeat center / contain`,
                        mask: `url(${ShoppingCart}) no-repeat center / contain`,
                      }}
                    />
                  </Badge>
                </IconButton>

                {/* Profile Dropdown */}

                <IconButton
                  id="user-menu-button"
                  onClick={(e) => setAnchorProfileEl(e.currentTarget)}
                  sx={{
                    ml: 2,
                    p: 0.5,
                    bgcolor: "transparent",
                    borderRadius: "50%",
                  }}
                >
                  {finalProfilePhoto ? (
                    <Box
                      component="img"
                      src={finalProfilePhoto}
                      key={finalProfilePhoto}
                      alt="Profile"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "1px solid #E0E0E0",
                      }}
                    />
                  ) : (
                    <UserIcon />
                  )}
                </IconButton>

                <Popover
                  open={openProfile}
                  anchorEl={anchorProfileEl}
                  onClose={() => setAnchorProfileEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      minWidth: 280,
                      borderRadius: "10px",
                      p: 2,
                      boxShadow: "0px 3px 28px -1px #3232470F",
                      bgcolor: "#F7F8FB",
                      mt: 0.5,
                    },
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{
                      padding: 1,
                    }}
                  >
                    {/* Create Profile Button */}
                    <Button
                      component={Link}
                      to="/user-profile"
                      onClick={() => setAnchorProfileEl(null)}
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        gap: 1.5,
                        p: 2,
                        borderRadius: "8px",
                        bgcolor: "#fff",
                        color: "#15585D !important",
                        border: "1px solid #E9E9E8",
                        "&:hover, &:active, &:focus": {
                          bgcolor: "#f0f0f0",
                          color: "#15585D !important",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {finalProfilePhoto ? (
                          <Box
                            component="img"
                            src={finalProfilePhoto}
                            key={finalProfilePhoto}
                            alt="Profile"
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "1px solid #E0E0E0",
                            }}
                          />
                        ) : (
                          <UserIcon />
                        )}
                      </Box>

                      {finalUserName && finalUserEmail ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 1,
                            cursor: "pointer",
                          }}
                        >
                          <Tooltip title={finalUserName} arrow>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 200,
                                cursor: "pointer",
                              }}
                            >
                              {finalUserName}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={finalUserEmail} arrow>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: "#4B4F50",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 200,
                                cursor: "pointer",
                              }}
                            >
                              {finalUserEmail}
                            </Typography>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography
                          sx={{ textDecoration: "underline", fontWeight: 700 }}
                        >
                          User Profile
                        </Typography>
                      )}
                    </Button>

                    <Box
                      onClick={() => {
                        setAnchorProfileEl(null);
                        navigate("/order-history");
                      }}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: "#15585D",
                          padding: 1,
                          borderRadius: "6px",
                          bgcolor: "#F0F6F7",
                          display: "flex",
                          justifyContent: "center",
                          textAlign: "center",
                          "&:hover, &:active, &:focus": {
                            bgcolor: "#E3EFF1",
                            color: "#15585D !important",
                          },
                        }}
                      >
                        Order History
                      </Typography>
                    </Box>

                    {/* Logout Button */}
                    <Button
                      onClick={handleLogout}
                      disabled={isLoading}
                      sx={{
                        justifyContent: "center",
                        textTransform: "none",
                        py: 0.5,
                        px: 1.5,
                        borderRadius: "10px",
                        border: "1px solid #F3C3C2",
                        color: "#F15B59",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: "#ffeaea" },
                      }}
                    >
                      Logout
                    </Button>
                  </Box>
                </Popover>
              </>
            ) : (
              <Button
                component={Link}
                to={`/?redirect=${encodeURIComponent(location.pathname + location.search)}`}
                variant="contained"
                sx={{
                  ml: 2,
                  backgroundColor: "#14585d",
                  color: "#fff !important",
                  textTransform: "uppercase",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#14585d",
                    color: "#fff !important",
                  },
                  "&:active, &:focus, &.Mui-focusVisible": {
                    backgroundColor: "#14585d",
                    color: "#fff !important",
                  },
                }}
              >
                User Login
              </Button>
            )}
          </Box>

          {/* Mobile Menu Button */}
          <Box
            sx={{ display: { xs: "flex", md: "none" }, alignItems: "center" }}
          >
            <IconButton
              onClick={toggleMobileDrawer(true)}
              sx={{ color: "#333" }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Full-Width Mobile Drawer */}
          <Drawer
            anchor="top"
            open={mobileDrawerOpen}
            onClose={toggleMobileDrawer(false)}
            PaperProps={{
              sx: {
                width: "100%",
                bgcolor: "#FBF4EA",
                borderBottom: "1px solid #ddd",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
              }}
              role="presentation"
              onKeyDown={toggleMobileDrawer(false)}
            >
              {/* Drawer Header with Close Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                  padding: "16px",
                  borderBottom: "1px solid #e0e0e0",
                  bgcolor: "#FBF4EA",
                }}
              >
                <IconButton onClick={toggleMobileDrawer(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Navigation List */}
              <List>
                {navLinks.map((link) => (
                  <ListItem key={link.label} disablePadding>
                    <ListItemButton
                      component={link.path === "/home" ? "button" : Link}
                      to={link.path !== "/home" ? link.path : undefined}
                      onClick={() => {
                        if (link.path === "/home") {
                          sessionStorage.removeItem("providerFilters");
                          navigate("/home", { state: { manualNav: true } });
                        }
                        setMobileDrawerOpen(false);
                      }}
                      sx={{ px: 3, py: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: "#15585D" }}>
                        {link.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{
                          fontSize: 16,
                          fontWeight: 500,
                          sx: { color: "#15585D !important" },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}

                {isLoggedIn ? (
                  <>
                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/cart"
                        sx={{ px: 3, py: 1.5 }}
                        onClick={toggleMobileDrawer(false)}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Badge badgeContent={cartRedux} color="error">
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                bgcolor: "#15585D",
                                WebkitMask: `url(${ShoppingCart}) no-repeat center / contain`,
                                mask: `url(${ShoppingCart}) no-repeat center / contain`,
                              }}
                            />
                          </Badge>
                        </ListItemIcon>
                        <ListItemText
                          primary="Cart"
                          primaryTypographyProps={{
                            fontSize: 16,
                            fontWeight: 500,
                            sx: { color: "#15585D !important" },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/user-profile"
                        sx={{ px: 3, py: 1.5 }}
                        onClick={toggleMobileDrawer(false)}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: "#15585D" }}>
                          <ProfileIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Profile"
                          primaryTypographyProps={{
                            fontSize: 16,
                            fontWeight: 500,
                            sx: { color: "#15585D !important" },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    
                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/order-history"
                        sx={{ px: 3, py: 1.5 }}
                        onClick={toggleMobileDrawer(false)}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: "#15585D" }}>
                          <HistoryIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Order History"
                          primaryTypographyProps={{
                            fontSize: 16,
                            fontWeight: 500,
                            sx: { color: "#15585D !important" },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={handleLogout}
                        disabled={isLoading}
                        sx={{ px: 3, py: 1.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <LogoutIcon fontSize="small" sx={{ color: "#15585D" }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Logout"
                          primaryTypographyProps={{
                            fontSize: 16,
                            fontWeight: 500,
                            sx: { color: "#15585D !important" },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </>
                ) : (
                  <ListItem disablePadding>
                    <ListItemButton
                      component={Link}
                      to={`/?redirect=${encodeURIComponent(location.pathname + location.search)}`}
                      sx={{ px: 3, py: 1.5 }}
                      onClick={toggleMobileDrawer(false)}
                    >
                      <ListItemText
                        primary="User Login"
                        primaryTypographyProps={{
                          fontSize: 16,
                          fontWeight: 500,
                          sx: { color: "#15585D !important" },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
      <EnableLocation
        openDialog={openLocationDialog}
        onClose={handleCloseLocationDialog}
      />
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </AppBar>
  );
}
